import { ref } from 'vue';
import { SIGN_IN_UPDATE_RESULT, updateAfterSignIn } from '@/utils/ota-sign-in-update';

/**
 * The "Updating" screen for the live update after sign-in
 * (see ota-sign-in-update.js).
 *
 * The screen appears only once a download actually starts, so a member with
 * nothing to download never sees it. It stays up on INSTALLING, because the
 * restart replaces the page anyway; any other outcome lifts it and the member
 * carries on where they are.
 *
 * Calls that overlap share one run: App.vue triggers this from a watcher, which
 * can fire more than once for the same sign-in.
 *
 * @param {{ update?: typeof updateAfterSignIn }} [options] injectable for tests
 */
export function useOtaSignInUpdate({ update = updateAfterSignIn } = {}) {
  const status = ref('idle');
  const percent = ref(0);
  let pending = null;

  function reset() {
    status.value = 'idle';
    percent.value = 0;
  }

  async function execute(saveReturnRoute) {
    let result = SIGN_IN_UPDATE_RESULT.FAILED;
    // Progress after the run has finished belongs to a download the member has
    // already been released from; it must not put the screen back up.
    let finished = false;
    try {
      result = await update({
        saveReturnRoute,
        onProgress: value => {
          if (finished) return;
          status.value = 'updating';
          percent.value = value;
        },
      });
    } finally {
      finished = true;
      if (result !== SIGN_IN_UPDATE_RESULT.INSTALLING) reset();
    }
    return result;
  }

  /** @param {() => void} saveReturnRoute records the page to come back to */
  function run(saveReturnRoute) {
    pending =
      pending ??
      execute(saveReturnRoute).finally(() => {
        pending = null;
      });
    return pending;
  }

  return { status, percent, run };
}
